import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import LoginLeftContainer from "./LoginLeftContainer";
import HomeHeader from "../../components/header/homeHeader/HomeHeader";
import config from "../../config";
import { useTranslation } from "react-i18next";

const ResetPassword = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    if (!token) {
      setError(t("Invalid or expired token."));
      setIsSubmitting(false);
      return;
    }

    if (newPassword.length < 6) {
      setError(t("Password must be at least 6 characters long"));
      setIsSubmitting(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t("Passwords don't match"));
      setIsSubmitting(false);
      return;
    }

    try {
      await axios.post(`${config.baseURL}/user/reset/password`, {
        token,
        newPassword,
      });

      setMessage(t("Password reset successful. Redirecting to login..."));
      setTimeout(() => navigate("/"), 2000);
    } catch (error) {
      setError(error.response?.data?.msg || t("Something went wrong"));
    } finally {
      setIsSubmitting(false);
    }
  };

  // const handleTogglePassword = () => {
  //   setShowPassword(!showPassword);
  // };

  const handleToggleNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <>
      <HomeHeader />
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          backgroundColor: "#f5f5f5",
          padding: "1rem",
          gap: "32px",
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", md: "50%" },
            display: "flex",
            justifyContent: "center",
          }}
        >
          <LoginLeftContainer />
        </Box>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: "100%",
            maxWidth: 400,
            borderRadius: "16px",
            textAlign: "center",
            border: "1px solid #A5DCF3",
            maxWidth: "460px",
          }}
        >
          <Typography
            variant="h5"
            fontWeight="600"
            gutterBottom
            sx={{ color: "#111214", fontSize: "30px" }}
          >
            {t("Create a New Password")}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            {t(
              "Enter your new password below to secure your account. Make sure it's strong and memorable.",
            )}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {message && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {message}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              type={showNewPassword ? "text" : "password"}
              placeholder={t("Enter your new password")}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
              required
              label={t("New Password")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleToggleNewPassword}>
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
                "& input::placeholder": {
                  fontSize: "14px",
                },
              }}
            />

            <TextField
              fullWidth
              type={showConfirmPassword ? "text" : "password"}
              placeholder={t("Re-enter your new password")}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
              label={t("Confirm Password")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleToggleConfirmPassword}>
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
                "& input::placeholder": {
                  fontSize: "14px",
                },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={
                isSubmitting ||
                !newPassword ||
                !confirmPassword ||
                newPassword.length < 6 ||
                newPassword !== confirmPassword ||
                !!message
              }
              sx={{
                mt: 3,
                height: "56px",
                borderRadius: "16px",
                backgroundColor:
                  isSubmitting ||
                  !newPassword ||
                  !confirmPassword ||
                  newPassword.length < 6 ||
                  newPassword !== confirmPassword ||
                  !!message
                    ? "#F5F5F5"
                    : "#FFFF00",
                color:
                  isSubmitting ||
                  !newPassword ||
                  !confirmPassword ||
                  newPassword.length < 6 ||
                  newPassword !== confirmPassword ||
                  !!message
                    ? "#666666"
                    : "#000000",
                fontWeight: 600,
                fontSize: "18px",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor:
                    isSubmitting ||
                    !newPassword ||
                    !confirmPassword ||
                    newPassword.length < 6 ||
                    newPassword !== confirmPassword ||
                    !!message
                      ? "#F5F5F5"
                      : "#FFFF00",
                  boxShadow: "none",
                },
              }}
            >
              {t("Save")}
            </Button>
          </form>
        </Paper>
      </Box>
    </>
  );
};

export default ResetPassword;
